/**
 * Order of Skate Worksheets — Documents → OOS panel inline tiles.
 *
 * Two cards rendered under the "Continue to Order of Skate" button so
 * the user can generate the OOS Worksheet and Timing Worksheet
 * without leaving Documents:
 *
 *   1. **Create Order of Skate Worksheets** — two brown download
 *      buttons (the OOS Worksheet and the Timing Worksheet) that hit
 *      the same backend endpoints the OOS page toolbar uses.
 *   2. **Email OOS Worksheets to Clubs** — club picker + attachment
 *      checkboxes (OOS default-on, Timing Worksheet optional). The
 *      "Email" button opens the shared ``CoveringEmailDialog`` against
 *      the new ``/api/oos-worksheets-email`` endpoint base.
 *
 * Visibility: tiles render whenever a competition is selected
 * (matches the owner's option (b) — they're available even when no
 * OOS exists; the buttons simply 404 with a friendly toast if the
 * user clicks before generating).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import CoveringEmailDialog from './CoveringEmailDialog';
import LastSentLine from './LastSentLine';


export default function OOSWorksheetsPanel({ competitionId }) {
  const [downloadingOOS, setDownloadingOOS] = useState(false);
  const [downloadingTiming, setDownloadingTiming] = useState(false);

  // Email-tile state
  const [clubsWithEntries, setClubsWithEntries] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // OOS default-on, Timing Worksheet default-off (owner spec).
  const [emailVersions, setEmailVersions] = useState({
    order_of_skate: true,
    timing_worksheet: false,
  });

  // Pull the same clubs list the email preflight uses — the endpoint
  // returns ``targets: [{club_id, name, missing_email}]``. Empty
  // gracefully if the comp has no entries yet.
  const fetchClubsWithEntries = async (compId) => {
    if (!compId) {
      setClubsWithEntries([]);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/oos-worksheets-email/${compId}/preflight`,
        { withCredentials: true }
      );
      setClubsWithEntries(res.data?.targets || []);
    } catch {
      setClubsWithEntries([]);
    }
  };

  useEffect(() => {
    fetchClubsWithEntries(competitionId);
    // Reset selection when comp changes so we don't carry stale club_id
    setSelectedClub('all');
  }, [competitionId]);

  // ============================================
  // Download handlers — same backend endpoints the OOS page uses
  // ============================================

  const downloadFromUrl = async (url, fallbackFilename, setBusy) => {
    setBusy(true);
    try {
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Parse Content-Disposition to use the backend-provided filename
      const disp = response.headers['content-disposition'] || '';
      const m = disp.match(/filename="?([^";\n]+)"?/);
      const filename = m ? m[1] : fallbackFilename;

      const blob = new Blob([response.data]);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(`${filename} downloaded`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      // Blob 404 responses don't expose detail directly; show a
      // sensible fallback message.
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Could not download worksheet — make sure an Order of Skate has been generated for this competition.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadOOSWorksheet = () => {
    if (!competitionId) return;
    downloadFromUrl(
      `${API}/order-of-skate/${competitionId}/oos-document`,
      'Order_of_Skate.xlsx',
      setDownloadingOOS
    );
  };

  const handleDownloadTimingWorksheet = () => {
    if (!competitionId) return;
    downloadFromUrl(
      `${API}/order-of-skate/${competitionId}/timing-worksheet`,
      'Timing_Worksheet.xlsx',
      setDownloadingTiming
    );
  };

  // Email button is disabled until at least one attachment is picked.
  const canEmail =
    emailVersions.order_of_skate || emailVersions.timing_worksheet;

  if (!competitionId) return null;

  return (
    <div className="space-y-6 mt-6" data-testid="oos-worksheets-panel">
      {/* TILE 1: Create Order of Skate Worksheets */}
      <Card
        className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
        data-testid="oosw-download-tile"
      >
        <div>
          <h4 className="text-base font-semibold text-stone-800">
            Create Order of Skate Worksheets
          </h4>
        </div>

        <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
          <Button
            onClick={handleDownloadOOSWorksheet}
            disabled={downloadingOOS}
            className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
            data-testid="oosw-download-oos-btn"
          >
            <Download className={`mr-2 h-4 w-4 ${downloadingOOS ? 'animate-pulse' : ''}`} />
            {downloadingOOS ? 'Generating...' : 'Order of Skate'}
          </Button>
          <div>
            <p className="text-sm font-medium text-stone-800">Generate the Order of Skate Worksheet</p>
            <p className="text-xs text-stone-600">
              Generates a worksheet outlining the order of skate with approximate timing. Calculations are not shown.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
          <Button
            onClick={handleDownloadTimingWorksheet}
            disabled={downloadingTiming}
            className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
            data-testid="oosw-download-timing-btn"
          >
            <Download className={`mr-2 h-4 w-4 ${downloadingTiming ? 'animate-pulse' : ''}`} />
            {downloadingTiming ? 'Generating...' : 'Timing Worksheet'}
          </Button>
          <div>
            <p className="text-sm font-medium text-stone-800">Generate the Timing Worksheet</p>
            <p className="text-xs text-stone-600">
              Generates a detailed order of skate with approximate timing including calculations.
            </p>
          </div>
        </div>
      </Card>

      {/* TILE 2: Email OOS Worksheets to Clubs */}
      <Card
        className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
        data-testid="oosw-email-tile"
      >
        <div>
          <h4 className="text-base font-semibold text-stone-800">
            Email Order of Events Worksheets to Clubs
          </h4>
          <p className="text-sm text-stone-600 mt-1">
            Generate the Order of Skate worksheets and send them out to clubs by email.
          </p>
          <LastSentLine competitionId={competitionId} kind="oos-worksheets" />
        </div>

        <div>
          <Label
            htmlFor="oosw-club-select"
            className="text-sm font-medium text-stone-700"
          >
            Clubs (Select individual clubs or All Clubs Option)
          </Label>
          <select
            id="oosw-club-select"
            className="w-full border rounded-lg px-3 py-2 mt-1 text-sm"
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            data-testid="oosw-club-select"
          >
            <option value="all">{`All Clubs (${clubsWithEntries.length} clubs)`}</option>
            {clubsWithEntries.map((c) => (
              <option key={c.club_id} value={c.club_id}>
                {`${c.name}${c.missing_email ? ' (no email on file)' : ''}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h5 className="text-sm font-semibold text-stone-800 mb-2">Attachments</h5>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-stone-800">
              <input
                type="checkbox"
                checked={emailVersions.order_of_skate}
                onChange={(e) =>
                  setEmailVersions((v) => ({ ...v, order_of_skate: e.target.checked }))
                }
                data-testid="oosw-attach-oos-checkbox"
              />
              Attach Order of Skate Worksheet
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-800">
              <input
                type="checkbox"
                checked={emailVersions.timing_worksheet}
                onChange={(e) =>
                  setEmailVersions((v) => ({ ...v, timing_worksheet: e.target.checked }))
                }
                data-testid="oosw-attach-timing-checkbox"
              />
              Attach Timing Worksheet (optional)
            </label>
          </div>
        </div>

        <Button
          onClick={() => setEmailDialogOpen(true)}
          disabled={!canEmail}
          className="btn-primary"
          data-testid="email-oos-worksheets-btn"
        >
          <Mail className="mr-2 h-4 w-4" />
          Email Order of Events Worksheets
        </Button>
      </Card>

      <CoveringEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        competitionId={competitionId}
        clubId={selectedClub}
        endpointBase="oos-worksheets-email"
        title="Email Order of Events Worksheets to Clubs"
        description="Compose the covering email. The selected worksheet .xlsx file(s) will be attached to every email."
        testIdPrefix="oosw-email"
        extraSendBody={{
          versions: [
            ...(emailVersions.order_of_skate ? ['order_of_skate'] : []),
            ...(emailVersions.timing_worksheet ? ['timing_worksheet'] : []),
          ],
        }}
      />
    </div>
  );
}
